/**
 * Call Service
 * 
 * Handles business logic for call management, including validation,
 * analysis, and coordination with call repositories.
 * 
 * Consolidates logic from 3 call systems:
 * - calls
 * - ai_calls
 * - sw_calls
 */

import { BaseService, ServiceError } from './BaseService';
import { repositories } from '../repositories';
import type { Call, CallFilters, CallStatistics } from '../repositories/CallRepository';

/**
 * Input types for creating calls
 */
export interface CreateCallInput {
  customer_id?: string;
  call_sid?: string;
  from_number: string;
  to_number: string;
  direction: 'inbound' | 'outbound';
  agent_id?: string;
  agent_type?: 'human' | 'ai';
  company_id?: string;
}

/**
 * Input types for updating calls
 */
export interface UpdateCallInput {
  status?: 'queued' | 'ringing' | 'in-progress' | 'completed' | 'no-answer' | 'busy' | 'failed';
  duration_seconds?: number;
  recording_url?: string;
  transcript?: string;
  sentiment_score?: number;
  summary?: string;
  end_time?: string;
}

/**
 * Input for analyzing call transcripts
 */
export interface AnalyzeTranscriptInput {
  call_id: string;
  transcript: string;
  analyze_sentiment?: boolean;
  extract_topics?: boolean;
}

/**
 * Result of transcript analysis
 */
export interface TranscriptAnalysis {
  sentiment_score?: number;
  topics?: string[];
  keywords?: string[];
  summary?: string;
  intent_detected?: string;
}

/**
 * Dashboard data for call analytics
 */
export interface CallDashboard {
  statistics: CallStatistics;
  recent_calls: Call[];
  active_calls: Call[];
  negative_sentiment_calls: Call[];
  average_handle_time: number;
  completion_rate: number;
  ai_vs_human_breakdown: {
    ai_calls: number;
    human_calls: number;
    ai_average_duration: number;
    human_average_duration: number;
  };
}

/**
 * Call Service Class
 * 
 * Provides business logic for call management operations.
 */
export class CallService extends BaseService {
  /**
   * Create a new call record
   */
  async createCall(input: CreateCallInput): Promise<Call> {
    try {
      // Validate input
      this.validateCallInput(input);

      // Normalize phone numbers
      const normalizedInput = {
        ...input,
        from_number: input.from_number ? this.normalizePhone(input.from_number) : null,
        to_number: input.to_number ? this.normalizePhone(input.to_number) : null,
        agent_type: input.agent_type || 'human',
        status: 'ringing' as const,
        interaction_type: 'call' as const,
        call_type: input.direction as 'inbound' | 'outbound',
        source_system: 'admin-portal',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Check for duplicate active calls (same call_sid)
      if (input.call_sid) {
        const existing = await repositories.calls.findByCallSid(input.call_sid);
        if (existing && existing.status && ['ringing', 'in-progress'].includes(existing.status)) {
          throw new ServiceError(
            'A call with this Call SID is already active',
            'DUPLICATE_CALL',
            409
          );
        }
      }

      // Create the call
      const call = await repositories.calls.create(normalizedInput as any);

      // Emit event
      await this.emit('call.created', { call });

      return call;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a call record
   */
  async updateCall(id: string, input: UpdateCallInput): Promise<Call> {
    try {
      // Get existing call
      const existing = await repositories.calls.findById(id);
      if (!existing) {
        throw new ServiceError('Call not found', 'NOT_FOUND', 404);
      }

      // Validate status transition
      if (input.status && existing.status) {
        this.validateStatusTransition(existing.status, input.status);
      }

      // Update the call
      const updated = await repositories.calls.update(id, {
        ...input,
        updated_at: new Date().toISOString(),
      } as any);

      // Emit event based on status change
      if (input.status === 'completed') {
        await this.emit('call.completed', { call: updated });
      } else if (input.status === 'failed') {
        await this.emit('call.failed', { call: updated });
      }

      return updated;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * End a call (mark as completed)
   */
  async endCall(id: string, durationSeconds: number): Promise<Call> {
    try {
      return await this.updateCall(id, {
        status: 'completed',
        duration_seconds: durationSeconds,
        end_time: new Date().toISOString(),
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Analyze call transcript
   */
  async analyzeTranscript(input: AnalyzeTranscriptInput): Promise<TranscriptAnalysis> {
    try {
      const { call_id, transcript, analyze_sentiment = true, extract_topics = true } = input;

      // Get the call
      const call = await repositories.calls.findById(call_id);
      if (!call) {
        throw new ServiceError('Call not found', 'NOT_FOUND', 404);
      }

      // Perform analysis
      const analysis: TranscriptAnalysis = {};

      if (analyze_sentiment) {
        // Simple sentiment analysis based on keywords
        const positiveWords = ['thank', 'great', 'excellent', 'good', 'happy', 'satisfied'];
        const negativeWords = ['bad', 'terrible', 'awful', 'poor', 'unhappy', 'angry'];
        
        const lowerTranscript = transcript.toLowerCase();
        const positiveCount = positiveWords.filter(word => lowerTranscript.includes(word)).length;
        const negativeCount = negativeWords.filter(word => lowerTranscript.includes(word)).length;

        if (positiveCount > negativeCount) {
          analysis.sentiment_score = 0.7 + (positiveCount * 0.1);
        } else if (negativeCount > positiveCount) {
          analysis.sentiment_score = 0.3 - (negativeCount * 0.1);
        } else {
          analysis.sentiment_score = 0.5;
        }
      }

      if (extract_topics) {
        // Extract topics from transcript
        const topics = this.extractTopics(transcript);
        analysis.topics = topics;
      }

      // Update call with analysis results
      await this.updateCall(call_id, {
        transcript,
        sentiment_score: analysis.sentiment_score,
        summary: analysis.summary,
      } as any);

      // Emit event
      await this.emit('call.analyzed', { call_id, analysis });

      return analysis;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get call dashboard data
   */
  async getCallDashboard(filters?: CallFilters): Promise<CallDashboard> {
    try {
      // Get statistics
      const statistics = await repositories.calls.getStatistics();

      // Get recent calls (last 10 days)
      const recent_calls = await repositories.calls.findRecent(10);

      // Get active calls
      const active_calls = await repositories.calls.findActive();

      // Get negative sentiment calls (sentiment score <= 0)
      const negative_sentiment_calls = await repositories.calls.findNegativeSentiment(0);

      // Calculate metrics
      const average_handle_time = statistics.averageDuration || 0;
      const completion_rate = statistics.total > 0
        ? (statistics.completed / statistics.total) * 100
        : 0;

      // Get AI vs Human breakdown
      const ai_calls_list = await repositories.calls.findByAgentType('ai');
      const human_calls_list = await repositories.calls.findByAgentType('human');

      const ai_vs_human_breakdown = {
        ai_calls: ai_calls_list.length,
        human_calls: human_calls_list.length,
        ai_average_duration: this.calculateAverageDuration(ai_calls_list),
        human_average_duration: this.calculateAverageDuration(human_calls_list),
      };

      return {
        statistics,
        recent_calls,
        active_calls,
        negative_sentiment_calls: negative_sentiment_calls.slice(0, 10),
        average_handle_time,
        completion_rate,
        ai_vs_human_breakdown,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get calls for a customer
   */
  async getCustomerCalls(customer_id: string, limit: number = 50): Promise<Call[]> {
    try {
      return await repositories.calls.findByCustomer(customer_id, { limit });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search calls by filters
   */
  async searchCalls(filters: CallFilters): Promise<Call[]> {
    try {
      return await repositories.calls.findByFilters(filters);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a call (soft delete by marking as inactive)
   */
  async deleteCall(id: string): Promise<void> {
    try {
      const call = await repositories.calls.findById(id);
      if (!call) {
        throw new ServiceError('Call not found', 'NOT_FOUND', 404);
      }

      // Don't allow deleting active calls
      if (call.status && ['ringing', 'in-progress'].includes(call.status)) {
        throw new ServiceError(
          'Cannot delete an active call',
          'INVALID_OPERATION',
          400
        );
      }

      await repositories.calls.delete(id);

      // Emit event
      await this.emit('call.deleted', { call_id: id });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Private: Validate call input
   */
  private validateCallInput(input: CreateCallInput): void {
    const errors: string[] = [];

    if (!input.from_number) {
      errors.push('from_number is required');
    }

    if (!input.to_number) {
      errors.push('to_number is required');
    }

    if (!['inbound', 'outbound'].includes(input.direction)) {
      errors.push('direction must be inbound or outbound');
    }

    if (input.agent_type && !['human', 'ai'].includes(input.agent_type)) {
      errors.push('agent_type must be human or ai');
    }

    if (errors.length > 0) {
      throw new ServiceError(
        `Validation failed: ${errors.join(', ')}`,
        'VALIDATION_ERROR',
        400
      );
    }
  }

  /**
   * Private: Validate status transition
   */
  private validateStatusTransition(
    currentStatus: string,
    newStatus: string
  ): void {
    const validTransitions: Record<string, string[]> = {
      queued: ['ringing', 'failed'],
      ringing: ['in-progress', 'failed', 'no-answer', 'busy'],
      'in-progress': ['completed', 'failed'],
      completed: [],
      failed: [],
      busy: [],
      'no-answer': [],
    };

    if (!validTransitions[currentStatus]?.includes(newStatus)) {
      throw new ServiceError(
        `Invalid status transition from ${currentStatus} to ${newStatus}`,
        'INVALID_TRANSITION',
        400
      );
    }
  }

  /**
   * Private: Extract topics from transcript
   */
  private extractTopics(transcript: string): string[] {
    // Simple keyword extraction (in production, use NLP)
    const commonTopics = [
      'billing', 'technical support', 'service request', 'appointment',
      'complaint', 'feedback', 'warranty', 'installation', 'maintenance',
      'pricing', 'quote', 'emergency'
    ];

    const lowerTranscript = transcript.toLowerCase();
    return commonTopics.filter(topic => lowerTranscript.includes(topic));
  }

  /**
   * Private: Calculate average duration
   */
  private calculateAverageDuration(calls: Call[]): number {
    if (calls.length === 0) return 0;
    const total = calls.reduce((sum, call) => sum + (call.duration_seconds || 0), 0);
    return Math.round(total / calls.length);
  }
}

/**
 * Singleton instance
 */
export const callService = new CallService();
